import type { AnalyticsData, AnalyticsEventDeclaration, AnalyticsKind, AnalyticsValue } from './declaration'
import { AnalyticsPageviewDeclaration, NoAnalyticsDeclaration } from './declaration'
import type { AnalyticsProvider } from './providers'

/** What a sink receives: a name and a flat payload, with nothing left to resolve. */
export type ResolvedEvent =
  { kind: 'custom'; type: string; data: AnalyticsData } | { kind: 'pageview'; type: string; data: AnalyticsData; url: string; path: string; title: string }

/**
 * Shapes an event for one provider, or returns null to drop it there. Per sink rather than per
 * router, because every case is a property of the provider: GA4 takes no dots, Firebase no arrays,
 * Amplitude both. The declaration decides *whether* a provider gets an event, this *what* it gets.
 */
export type SinkProcessor = (event: ResolvedEvent) => ResolvedEvent | null

export interface AnalyticsSink {
  readonly provider: AnalyticsProvider
  /** Applied by the router, so a sink cannot forget it and no caller can send around it. */
  process?: SinkProcessor
  send(event: ResolvedEvent): void
}

/**
 * Resolves declarations and hands them to sinks — the only place a declaration becomes an event.
 *
 * `providers` overrides the declared list for the one case source cannot answer: the webview bridge
 * is handed a provider list by the app, per event.
 */
export class AnalyticsRouter {
  constructor(
    private options: {
      sinks: readonly AnalyticsSink[]
      /** Read per event rather than once, since the audience follows an account switch. */
      context?: () => AnalyticsData
      /**
       * Rewrites the provider set before dispatch, for a destination that is not a sink: the
       * webview bridge posts the event to the app and drops the two providers the app then owns.
       */
      reroute?: (event: ResolvedEvent, providers: readonly AnalyticsProvider[]) => readonly AnalyticsProvider[]
    }
  ) {}

  send(declaration: AnalyticsEventDeclaration | AnalyticsPageviewDeclaration, providers?: readonly AnalyticsProvider[]) {
    // The one branch here, and it is not about any widget: a control that reports nothing.
    if (declaration instanceof NoAnalyticsDeclaration) return

    const chosen = providers ?? declaration.providers
    if (!chosen.length) return

    const event = this.resolve(declaration)
    const routed = this.options.reroute?.(event, chosen) ?? chosen
    if (routed.length) this.dispatch(event, routed)
  }

  private resolve(declaration: AnalyticsEventDeclaration | AnalyticsPageviewDeclaration): ResolvedEvent {
    // `instanceof` rather than a `kind` check: it narrows `spec()` too, so neither branch casts.
    if (declaration instanceof AnalyticsPageviewDeclaration) {
      const spec = declaration.spec()
      return { ...spec, kind: 'pageview', data: this.withContext(spec.data) }
    }

    const spec = declaration.spec()
    return { kind: 'custom', type: spec.type, data: this.withContext(spec.data) }
  }

  /** Context first, so an event's own field wins a name collision rather than being overwritten. */
  private withContext(data: AnalyticsData | undefined): AnalyticsData {
    return { ...this.options.context?.(), ...data }
  }

  private dispatch(event: ResolvedEvent, providers: readonly AnalyticsProvider[]) {
    for (const sink of this.options.sinks) {
      if (!providers.includes(sink.provider)) continue
      // Not `?? event`: null is the sink refusing the event, not the absence of a result.
      const shaped = sink.process ? sink.process(event) : event
      if (shaped) sink.send(shaped)
    }
  }
}

/**
 * `{ perks_ids: ['a', 'b'] }` becomes `{ 'perks_ids.0': 'a', 'perks_ids.1': 'b' }`, for a provider
 * whose parameters are scalar-only. A sink that accepts arrays should not call this: flattening
 * loses the fact that it was one list.
 */
export const flattenEventData = (data: AnalyticsData): Record<string, AnalyticsValue> => {
  const flat: Record<string, AnalyticsValue> = {}
  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) value.forEach((item, index) => (flat[`${key}.${index}`] = item))
    else flat[key] = value as AnalyticsValue
  }
  return flat
}

/** What GA4 is given instead of the event type; the raw one still rides along as `event_id`. */
export const googleEventName = (type: string) => type.replace(/[^a-z0-9]/g, '_')

/** Merges fields under the event's own, so an event field wins a name collision. */
export const mergeData =
  (extra: () => AnalyticsData): SinkProcessor =>
  event => ({ ...event, data: { ...extra(), ...event.data } })

/** Renames an event on its way to one provider, leaving the payload alone. */
export const renameEvent =
  (rename: (type: string) => string): SinkProcessor =>
  event => ({ ...event, type: rename(event.type) })

export const flattenPayload: SinkProcessor = event => ({ ...event, data: flattenEventData(event.data) })

/** Drops an event that is not of this kind — a pageview sink and an event sink are not the same. */
export const onlyKind =
  (kind: AnalyticsKind): SinkProcessor =>
  event =>
    event.kind === kind ? event : null

/** Left to right, stopping at the first step that drops the event. */
export const composeProcessors =
  (...steps: readonly SinkProcessor[]): SinkProcessor =>
  event =>
    steps.reduce<ResolvedEvent | null>((carried, step) => (carried ? step(carried) : null), event)
